<template>
    <div class="pickup-timer-item" :class="{ 'urgent': isUrgent && !isLoading, 'complete': isComplete && !isLoading, 'loading': isLoading }">
        <!-- Loading Skeleton -->
        <div v-if="isLoading" class="timer-skeleton">
            <div class="skeleton-header">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-text-lines">
                    <div class="skeleton-line skeleton-title"></div>
                    <div class="skeleton-line skeleton-subtitle"></div>
                </div>
            </div>
            <div class="skeleton-display">
                <div class="skeleton-segments">
                    <div class="skeleton-segment"></div>
                    <div class="skeleton-separator"></div>
                    <div class="skeleton-segment"></div>
                    <div class="skeleton-separator"></div>
                    <div class="skeleton-segment"></div>
                </div>
            </div>
        </div>
        
        <!-- Timer State -->
        <div v-else>
            <div class="timer-header">
                <div class="timer-icon">
                    <v-avatar :color="getTimerColor" size="32">
                        <v-icon color="white" size="18">
                            {{ getTimerIcon }}
                        </v-icon>
                    </v-avatar>
                </div>
                <div class="timer-info">
                    <h3 class="timer-title">{{ getTimerMessage }}</h3>
                </div>
            </div>
            
            <div class="timer-display">
                <div class="time-segments">
                    <div class="time-segment">
                        <div class="time-value">{{ timeComponents.hours }}</div>
                        <div class="time-label">Stunden</div>
                    </div>
                    <div class="time-separator">:</div>
                    <div class="time-segment">
                        <div class="time-value">{{ timeComponents.minutes }}</div>
                        <div class="time-label">Minuten</div>
                    </div>
                    <div class="time-separator">:</div>
                    <div class="time-segment">
                        <div class="time-value">{{ timeComponents.seconds }}</div>
                        <div class="time-label">Sekunden</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PickupTimerItem',
    data() {
        return {
            isLoading: true,
            currentTime: new Date(),
            targetTime: null,
            timer: null
        }
    },
    computed: {
        remainingTime() {
            if (!this.targetTime) return 0;
            const remaining = this.targetTime - this.currentTime;
            return Math.max(0, remaining);
        },
        timeComponents() {
            const totalSeconds = Math.floor(this.remainingTime / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            
            return {
                hours: hours.toString().padStart(2, '0'),
                minutes: minutes.toString().padStart(2, '0'),
                seconds: seconds.toString().padStart(2, '0')
            };
        },
        isUrgent() {
            // Less than 2 hours remaining
            return this.remainingTime < (2 * 60 * 60 * 1000) && this.remainingTime > 0;
        },
        isComplete() {
            return this.remainingTime <= 0;
        },
        targetTimeFormatted() {
            if (!this.targetTime) return '15:00';
            return this.targetTime.toLocaleTimeString('de-DE', { 
                hour: '2-digit', 
                minute: '2-digit' 
            });
        },
        getTimerColor() {
            if (this.isComplete) return 'success';
            if (this.isUrgent) return 'error';
            return 'primary';
        },
        getTimerIcon() {
            if (this.isComplete) return 'mdi-check-circle';
            if (this.isUrgent) return 'mdi-clock-alert';
            return 'mdi-clock-outline';
        },
        getTimerMessage() {
            if (this.isComplete) return 'Abholzeit erreicht!';
            if (this.isUrgent) return 'Abholung in Kürze';
            return 'Abholung in';
        }
    },
    mounted() {
        // Calculate target time (15:00 today or tomorrow)
        this.calculateTargetTime();
        
        // Start the timer
        this.startTimer();
        
        // Hide skeleton after brief delay
        setTimeout(() => {
            this.isLoading = false;
        }, 800);
    },
    beforeDestroy() {
        // Clean up timer
        this.stopTimer();
    },
    methods: {
        calculateTargetTime() {
            // Set target to today at 15:00 (3 PM)
            const now = new Date();
            const today15 = new Date();
            today15.setHours(15, 0, 0, 0); // 15:00:00.000
            
            // If it's already past 15:00 today, set target to tomorrow at 15:00
            if (now >= today15) {
                const tomorrow15 = new Date(today15);
                tomorrow15.setDate(tomorrow15.getDate() + 1);
                this.targetTime = tomorrow15;
                console.log('🕒 Timer target set to tomorrow 15:00:', tomorrow15.toLocaleString());
            } else {
                this.targetTime = today15;
                console.log('🕒 Timer target set to today 15:00:', today15.toLocaleString());
            }
        },
        
        startTimer() {
            this.timer = setInterval(() => {
                this.currentTime = new Date();
                
                // If target time has passed, recalculate for next day
                if (this.targetTime && this.currentTime >= this.targetTime) {
                    this.calculateTargetTime();
                }
            }, 1000);
        },
        
        stopTimer() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        }
    },
    watch: {
        targetTime() {
            // Brief skeleton when target time changes
            this.isLoading = true;
            setTimeout(() => {
                this.isLoading = false;
            }, 200);
        }
    }
}
</script>

<style scoped>
.pickup-timer-item {
    background: linear-gradient(135deg, #ffffff, #f8f9fa);
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.pickup-timer-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.pickup-timer-item.urgent {
    border-color: #f44336;
    background: linear-gradient(135deg, #ffebee, #ffffff);
    animation: urgentPulse 2s ease-in-out infinite;
}

.pickup-timer-item.complete {
    border-color: #4caf50;
    background: linear-gradient(135deg, #f1f8e9, #ffffff);
}

.pickup-timer-item.loading {
    border-color: #1976d2;
    background: linear-gradient(135deg, #e3f2fd, #ffffff);
}

.timer-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.timer-icon {
    flex-shrink: 0;
}

.timer-info {
    flex: 1;
}

.timer-title {
    font-size: 14px;
    font-weight: 700;
    color: #333;
    margin: 0;
}

.timer-subtitle {
    font-size: 10px;
    color: #666;
    margin: 0;
    font-weight: 500;
}

.timer-display {
    margin-bottom: 0;
}

/* Skeleton Loading Styles */
.timer-skeleton {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.skeleton-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.skeleton-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
}

.skeleton-text-lines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.skeleton-line {
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
}

.skeleton-title {
    width: 70%;
    height: 14px;
}

.skeleton-subtitle {
    width: 50%;
    height: 10px;
}

.skeleton-display {
    margin-top: 4px;
}

.skeleton-segments {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
}

.skeleton-segment {
    flex: 1;
    height: 50px;
    border-radius: 8px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
}

.skeleton-separator {
    width: 8px;
    height: 20px;
    border-radius: 4px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    flex-shrink: 0;
}

@keyframes skeleton-loading {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.time-segments {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
}

.time-segment {
    background: rgba(25, 118, 210, 0.1);
    border-radius: 8px;
    padding: 8px 4px;
    text-align: center;
    flex: 1;
    min-width: 0;
}

.time-value {
    font-size: 24px;
    font-weight: 700;
    color: #1976d2;
    line-height: 1;
    font-family: 'Monaco', 'Consolas', monospace;
}

.time-label {
    font-size: 10px;
    color: #666;
    font-weight: 600;
    margin-top: 2px;
}

.time-separator {
    font-size: 20px;
    font-weight: 700;
    color: #1976d2;
    margin: 0 2px;
    flex-shrink: 0;
}


/* Urgent state styling */
.pickup-timer-item.urgent .time-value {
    color: #f44336;
}

.pickup-timer-item.urgent .time-separator {
    color: #f44336;
}

.pickup-timer-item.urgent .time-segment {
    background: rgba(244, 67, 54, 0.1);
}

/* Complete state styling */
.pickup-timer-item.complete .time-value {
    color: #4caf50;
}

.pickup-timer-item.complete .time-separator {
    color: #4caf50;
}

.pickup-timer-item.complete .time-segment {
    background: rgba(76, 175, 80, 0.1);
}

/* Animations */
@keyframes urgentPulse {
    0%, 100% { border-color: #f44336; }
    50% { border-color: #ff8a80; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .pickup-timer-item {
        padding: 10px;
    }
    
    .timer-title {
        font-size: 12px;
    }
    
    .timer-subtitle {
        font-size: 9px;
    }
    
    .time-value {
        font-size: 20px;
    }
    
    .time-segment {
        padding: 6px 4px;
        flex: 1;
        min-width: 0;
    }
    
    .time-label {
        font-size: 9px;
    }
    
    .time-separator {
        font-size: 18px;
        margin: 0 1px;
    }
}

@media (max-width: 480px) {
    .pickup-timer-item {
        padding: 8px;
    }
    
    .timer-header {
        gap: 6px;
        margin-bottom: 6px;
    }
    
    .timer-title {
        font-size: 11px;
    }
    
    .time-value {
        font-size: 18px;
    }
    
    .time-segments {
        gap: 4px;
    }
    
    .time-segment {
        padding: 4px 2px;
        flex: 1;
        min-width: 0;
    }
    
    .time-separator {
        font-size: 16px;
        margin: 0 1px;
    }
}
</style>